import { baseApi, resourceListTag, resourceScopeTag, transportRequest } from '@krp/client-runtime'

import { decodeAccess, decodeCashier, decodeRoster, type OwnCashier, type OwnCashierAccess, type OwnCashierRoster } from './contracts'
import { OWN_CASHIER_URLS as URLS } from './urls'

/** Тег списка кассиров ресурса: `<rid>:LIST` — правка в одном ресурсе не сбрасывает соседний. */
export const OWN_CASHIER_TAG = 'OwnCashier' as const

interface ResourceArgs {
  resourceId: string
}

export interface OwnCashierCreateArgs extends ResourceArgs {
  /** Один на попытку: повтор после «ответ не получен» возвращает тот же результат, а не второго кассира. */
  operationId: string
  name: string
}

export interface OwnCashierChangeArgs extends ResourceArgs {
  operationId: string
  cashierId: string
  expectedRevision: number
  name: string
  archived: boolean
}

/** Кого касается отзыв в Cashiers: ключ кассира — `id:<GUID узла>`, как в каталоге источника. */
export interface OwnCashierAccessIdentity extends ResourceArgs {
  cashierId: string
}

export interface OwnCashierRevokeArgs extends OwnCashierAccessIdentity {
  operationId: string
  expectedVersion: number
}

export const cashierKeyOf = (cashierId: string): string => `id:${cashierId.toLowerCase()}`

const identityOf = ({ resourceId, cashierId }: OwnCashierAccessIdentity) => ({
  resourceId,
  cashierKey: cashierKeyOf(cashierId),
  sourceCashierId: cashierId.toLowerCase(),
})

export const ownCashiersApi = baseApi
  .enhanceEndpoints({ addTagTypes: [OWN_CASHIER_TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      ownCashierRoster: build.query<OwnCashierRoster, ResourceArgs>({
        query: ({ resourceId }) => ({ url: URLS.roster(resourceId), decode: decodeRoster }),
        providesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_CASHIER_TAG, resourceId), resourceScopeTag(resourceId)],
      }),
      ownCreateCashier: build.mutation<OwnCashier, OwnCashierCreateArgs>({
        query: ({ resourceId, operationId, name }) => ({
          url: URLS.roster(resourceId),
          method: 'POST',
          body: { operationId, name },
          decode: (raw) => decodeCashier(raw),
        }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_CASHIER_TAG, resourceId)],
      }),
      ownChangeCashier: build.mutation<OwnCashier, OwnCashierChangeArgs>({
        query: ({ resourceId, cashierId, operationId, expectedRevision, name, archived }) => ({
          url: URLS.rosterItem(resourceId, cashierId),
          method: 'PUT',
          body: { operationId, expectedRevision, name, archived },
          decode: (raw) => decodeCashier(raw),
        }),
        // Конфликт версии тоже перечитывает список: человек видит, что изменил сосед.
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_CASHIER_TAG, resourceId)],
      }),
    }),
  })

export const { ownCashierRoster, ownCreateCashier, ownChangeCashier } = ownCashiersApi.endpoints

/** Версия настроек доступа кассира в Cashiers — мимо кэша: нужна ровно для одного отзыва. */
export const ownCashierAccessVersion = (identity: OwnCashierAccessIdentity) =>
  transportRequest<OwnCashierAccess>({ url: URLS.accessSettings, params: identityOf(identity), decode: decodeAccess })

/**
 * Отозвать PIN и действующий доступ кассира (`POST /api/cashiers/v1/settings/revoke`). Вызывать ДО архивирования: архивного
 * кассира Cashiers в каталоге уже не находит. Без повтора: при таймауте решает человек (тот же `operationId` вернёт прежний итог).
 */
export const ownRevokeCashierAccess = (args: OwnCashierRevokeArgs) =>
  transportRequest<OwnCashierAccess>({
    url: URLS.accessRevoke,
    method: 'POST',
    body: { ...identityOf(args), expectedVersion: args.expectedVersion, operationId: args.operationId },
    decode: decodeAccess,
    noRetry: true,
  })
